import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { 
  Loader2,
  Save,
  ArrowLeft,
  Building2,
  MapPin,
  Phone,
  Mail,
  User,
  Package
} from 'lucide-react';
import { storeApiService, Store, StoreCreateRequest, StoreUpdateRequest } from '../services/storeApi';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { userApiService, User as UserType } from '../services';

interface StoreFormData {
  storeName: string;
  storeType: 'RETAIL' | 'WAREHOUSE' | 'DISTRIBUTION_CENTER';
  location: string;
  phone: string;
  email: string;
  managerId: string;
  status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE';
  capacity: string;
  notes: string;
}

export function StoreForm() {
  const navigate = useNavigate();
  const { id, mode } = useParams<{ id: string; mode: string }>();
  const { user } = useAuth();
  const { selectedBranch, setSelectedBranch } = useBranch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isNewMode] = useState(!id || mode === 'new');
  const [users, setUsers] = useState<UserType[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [storeManagerId, setStoreManagerId] = useState<number | null>(null);
  const [managerIdSet, setManagerIdSet] = useState(false);

  const [formData, setFormData] = useState<StoreFormData>({
    storeName: '',
    storeType: 'RETAIL',
    location: '',
    phone: '',
    email: '',
    managerId: '',
    status: 'ACTIVE',
    capacity: '',
    notes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch users by branch
  const fetchUsers = useCallback(async (branchId: number | null) => {
    if (!branchId) {
      setUsers([]);
      return;
    }

    try {
      setLoadingUsers(true);
      const response = await userApiService.getUsers(1, 100, branchId);
      if (response.success) {
        setUsers(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch users');
        setUsers([]);
      }
    } catch (error) {
      // console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Fetch users when branch changes
  useEffect(() => {
    if (selectedBranch !== null) {
      fetchUsers(selectedBranch);
    } else {
      setUsers([]);
    }
  }, [selectedBranch, fetchUsers]);

  // Set managerId in formData once users are loaded (for edit mode)
  useEffect(() => {
    if (storeManagerId !== null && users.length > 0 && !managerIdSet) {
      const managerExists = users.some(user => user.id === storeManagerId);
      if (managerExists) {
        setFormData(prev => ({ ...prev, managerId: storeManagerId.toString() }));
        setManagerIdSet(true);
      }
    }
  }, [users, storeManagerId, managerIdSet]);

  // Fetch store data for edit mode
  const fetchStore = useCallback(async (storeId: string) => {
    try {
      setLoading(true);
      const store = await storeApiService.getStoreById(storeId);
        // Store managerId separately to set it after users load
        // Check both manager object and managerId field for compatibility
        const managerId = store.manager?.id || store.managerId;
        if (managerId) {
          setStoreManagerId(managerId);
        } else {
          setStoreManagerId(null);
        }
        
        if (store.branchId) {
          // Ensure branch context matches the store and load users for that branch
          if (selectedBranch !== store.branchId) {
            setSelectedBranch(store.branchId);
          }
          fetchUsers(store.branchId);
        } else {
          setUsers([]);
        }
        
        setFormData({
          storeName: store.storeName || '',
          storeType: store.storeType || 'RETAIL',
          location: store.location || '',
          phone: store.phone || '',
          email: store.email || '',
          managerId: '', // Will be set by useEffect once users are loaded
          status: store.status || 'ACTIVE',
          capacity: store.capacity?.toString() || '',
          notes: store.notes || ''
        });
    } catch (error) {
      // console.error('Error fetching store:', error);
      toast.error('Failed to fetch store details');
      navigate('/stores');
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedBranch, setSelectedBranch, fetchUsers]);

  useEffect(() => {
    if (!isNewMode && id) {
      fetchStore(id);
    } else {
      // Reset storeManagerId and flag for new store
      setStoreManagerId(null);
      setManagerIdSet(false);
    }
  }, [isNewMode, id, fetchStore]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};


    if (!formData.storeName.trim()) {
      newErrors.storeName = 'Store name is required';
    }

    if (!formData.storeType) {
      newErrors.storeType = 'Store type is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    if (formData.capacity && (isNaN(Number(formData.capacity)) || Number(formData.capacity) < 0)) {
      newErrors.capacity = 'Capacity must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof StoreFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    try {
      setSaving(true);
      const storeData = {
        ...formData,
        managerId: formData.managerId ? Number(formData.managerId) : undefined,
        // Prefer currently selected branch; fall back to user's default branch; lastly 0
        branchId: selectedBranch ?? user?.defaultBranchId ?? 0,
        capacity: formData.capacity ? Number(formData.capacity) : undefined
      };

      if (isNewMode) {
        await storeApiService.createStore(storeData as StoreCreateRequest);
        toast.success('Store created successfully');
      } else {
        await storeApiService.updateStore(id!, {
          ...storeData,
          id: Number(id)
        } as StoreUpdateRequest);
        toast.success('Store updated successfully');
      }

      navigate('/stores');
    } catch (error) {
      // console.error('Error saving store:', error);
      toast.error('Failed to save store');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/stores');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
            <p className="text-gray-500">Loading store details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              onClick={handleBack}
              variant="outline"
              className="border-primary-200 text-primary-700 hover:bg-primary-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
                {isNewMode ? 'Create Store' : 'Edit Store'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isNewMode ? 'Add a new store to your system' : 'Update store information'}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-200"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Store'}
          </Button>
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Basic Information */}
          <Card className="lg:col-span-2 border-primary-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
              <CardTitle className="flex items-center">
                <Building2 className="h-5 w-5 mr-2" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="storeName" className="text-sm font-medium text-gray-700">
                    Store Name *
                  </Label>
                  <Input
                    id="storeName"
                    value={formData.storeName}
                    onChange={(e) => handleInputChange('storeName', e.target.value)}
                    placeholder="e.g., Main Warehouse"
                    className={`border-primary-200 focus:border-primary-400 focus:ring-primary-400 ${errors.storeName ? 'border-red-500' : ''}`}
                  />
                  {errors.storeName && (
                    <p className="text-sm text-red-600">{errors.storeName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="storeType" className="text-sm font-medium text-gray-700">
                    Store Type *
                  </Label>
                  <Select
                    value={formData.storeType}
                    onValueChange={(value) => handleInputChange('storeType', value as any)}
                  >
                    <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder="Select store type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RETAIL">Retail</SelectItem>
                      <SelectItem value="WAREHOUSE">Warehouse</SelectItem>
                      <SelectItem value="DISTRIBUTION_CENTER">Distribution Center</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.storeType && (
                    <p className="text-sm text-red-600">{errors.storeType}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status" className="text-sm font-medium text-gray-700">
                    Status
                  </Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleInputChange('status', value as any)}
                  >
                    <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="INACTIVE">Inactive</SelectItem>
                      <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location" className="text-sm font-medium text-gray-700">
                  Location
                </Label>
                <Textarea
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Enter complete address with city, state, pincode"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className={`border-primary-200 focus:border-primary-400 focus:ring-primary-400 ${errors.phone ? 'border-red-500' : ''}`}
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="store@company.com"
                    className={`border-primary-200 focus:border-primary-400 focus:ring-primary-400 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capacity" className="text-sm font-medium text-gray-700">
                    Capacity
                  </Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => handleInputChange('capacity', e.target.value)}
                    placeholder="1000"
                    className={`border-primary-200 focus:border-primary-400 focus:ring-primary-400 ${errors.capacity ? 'border-red-500' : ''}`}
                    min="0"
                  />
                  {errors.capacity && (
                    <p className="text-sm text-red-600">{errors.capacity}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-sm font-medium text-gray-700">
                    Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Additional notes..."
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact & Additional Information */}
          <Card className="border-primary-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
              <CardTitle className="flex items-center">
                <User className="h-5 w-5 mr-2" />
                Contact & Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="managerName" className="text-sm font-medium text-gray-700">
                    Manager Name
                  </Label>
                  <Select
                    value={formData.managerId}
                    onValueChange={(value) => handleInputChange('managerId', value)}
                    disabled={!selectedBranch || loadingUsers}
                  >
                    <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder={!selectedBranch ? "Select a branch first" : loadingUsers ? "Loading users..." : "Select manager"} />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.fullName || `${user.firstName} ${user.lastName}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!selectedBranch && (
                    <p className="text-sm text-amber-600">Please select a branch to load users</p>
                  )}
                  {selectedBranch && !loadingUsers && users.length === 0 && (
                    <p className="text-sm text-amber-600">No users found for this branch</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
